import React, { useState } from 'react';

interface CoverDesignAddonProps {
  isAdded: boolean;
  selectedType: string | null;
  onAdd: (type: string) => void;
  onRemove: () => void;
}

const S3_BASE = 'https://s3.ap-south-1.amazonaws.com/assets.indiepress.in-122166278142-ap-south-1-an';

const PACKAGES = [
  {
    id: 'minimalistic',
    label: 'Minimalistic',
    description: 'Clean layouts, strong negative space, restrained typography',
    images: [
      `${S3_BASE}/minimalistic/minimalistic1.jpg`,
      `${S3_BASE}/minimalistic/minimalistic2.jpg`,
      `${S3_BASE}/minimalistic/minimalistic3.jpg`,
      `${S3_BASE}/minimalistic/minimalistic4.jpg`,
    ],
  },
  {
    id: 'photography',
    label: 'Photographic',
    description: 'High-quality photography with cinematic, emotional tone',
    images: [
      `${S3_BASE}/photography/photography1.jpg`,
      `${S3_BASE}/photography/photography2.jpg`,
      `${S3_BASE}/photography/photography3.jpg`,
    ],
  },
  {
    id: 'typography',
    label: 'Typographic',
    description: 'Bold expressive fonts as the primary design element',
    images: [
      `${S3_BASE}/typography/typography1.jpg`,
      `${S3_BASE}/typography/typography2.jpg`,
      `${S3_BASE}/typography/typography3.jpg`,
    ],
  },
];

const CoverDesignAddon: React.FC<CoverDesignAddonProps> = ({ isAdded, selectedType, onAdd, onRemove }) => {
  const [activeImage, setActiveImage] = useState<Record<string, number>>({
    minimalistic: 0,
    photography: 0,
    typography: 0,
  });

  const cycleImage = (e: React.MouseEvent, pkgId: string, total: number) => {
    e.stopPropagation();
    setActiveImage(prev => ({ ...prev, [pkgId]: (prev[pkgId] + 1) % total }));
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-2.5">
        <label className="block text-gray-900 text-sm font-medium">Book Cover Design Package</label>
        <p className="text-xs text-gray-400 mt-0.5">Professional cover design by our team · delivered within 5 days</p>
      </div>

      <div className="flex-1 flex flex-col gap-2">
        {PACKAGES.map((pkg) => {
          const isSelected = isAdded && selectedType === pkg.id;
          const imgIndex = activeImage[pkg.id];

          return (
            <div
              key={pkg.id}
              onClick={() => !isSelected && onAdd(pkg.id)}
              className={`flex-1 flex items-stretch border rounded-lg overflow-hidden cursor-pointer transition-all ${
                isSelected
                  ? 'border-orange-400 bg-white shadow-sm'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {/* Portrait thumbnail — click to cycle */}
              <div
                className="relative w-16 flex-shrink-0 group"
                onClick={(e) => cycleImage(e, pkg.id, pkg.images.length)}
                title="Click to browse examples"
              >
                <img
                  src={pkg.images[imgIndex]}
                  alt={`${pkg.label} example`}
                  className="w-full h-full object-cover object-top"
                />
                {/* Cycle hint overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-end justify-center pb-1">
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {pkg.images.map((_, i) => (
                      <span key={i} className={`w-1 h-1 rounded-full ${i === imgIndex ? 'bg-white' : 'bg-white bg-opacity-50'}`} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 px-3 py-2.5 flex flex-col justify-between min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-gray-900 text-sm font-medium">{pkg.label}</p>
                    <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{pkg.description}</p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-orange-500 text-sm font-semibold leading-tight">₹ 2,500</p>
                    <p className="text-gray-400 text-xs">+ GST</p>
                  </div>
                </div>
                <div className="mt-2">
                  {isSelected ? (
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-orange-600 text-xs font-medium">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        Selected
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); onRemove(); }}
                        className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">Tap to select</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CoverDesignAddon;
